import * as utils from '../utils';
import * as appiumCapabilitiesBuilder from '../commons/appiumCapabilitiesBuilder';
import { DOMParser } from 'linkedom';
import { BaseWorker } from './BaseWorker';
import { getLogger } from '../commons/logger';
import { reporter } from '../reports/reporter';
import { AppiumTestPlayer } from '../player/appiumTestPlayer';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { mobileGridService } from '../services/mobileGridService';
import { gridTypes, timeoutMessages } from '../commons/constants';
import * as concurrencyCompanyService from '../services/concurrencyCompanyService';
import type { FailedSessionPlayerTestResult } from 'clickim/src/background/session/sessionPlayer';
import type { SerializedGrid } from '../services/gridService';
import type { TestResult } from 'clickim/src/common/models/TestResult';
import type { NativeAppData, TestRunHandler } from '../testRunHandler';
import type { WebdriverAppiumSessionCapabilities } from 'clickim/src/background/session/MobileAgentSessionManager';
import type { Capabilities } from '@wdio/types';
import type { PlaybackMobileDevice } from 'services/src/grid/mobileGridService';
import type { AppiumApi } from 'clickim/src/common/mobile/appiumApi';

type RemoteOptions = Capabilities.RemoteConfig;

const logger = getLogger('worker-appium');

export class WorkerAppium extends BaseWorker<AppiumTestPlayer> {
    private activeSession!: WebdriverIO.Browser;
    private device!: PlaybackMobileDevice;
    private keepAliveIndex?: ReturnType<typeof setInterval>;

    override initPlayer(testRunHandler: TestRunHandler) {
        return new AppiumTestPlayer(
            this.id,
            testRunHandler.runParams,
            this.options.shouldMonitorPerformance,
            undefined,
            testRunHandler.retryCount,
            testRunHandler.previousTestResultId,
        );
    }

    override async getBrowserOnce(
        testRunHandler: TestRunHandler,
        customExtensionLocalLocation: string | undefined,
        appiumTestPlayer: AppiumTestPlayer,
        gridInfo: SerializedGrid,
    ) {
        const projectType = this.options.projectData!.type!;
        const shouldRunInEnhancedMode =
            (projectType === 'android' || projectType === 'ios') &&
            (await testRunHandler.isTestRecordedWithEnhancedMode());

        if (gridInfo.type !== gridTypes.TESTIM_TVC && shouldRunInEnhancedMode) {
            throw Error('The provided test(s) were authored in Enhanced Mode and can run only on VMG!');
        }

        reporter.onGetSession(this.id, this.testName, testRunHandler.runMode);
        const { driver } = appiumTestPlayer;
        const appPath = await mobileGridService.getAppIdFromGridOrTestimPublicLink({
            testRunHandler,
            options: this.options,
            pendingAppUploads: this.executionQueue.pendingAppUploads,
        });
        const androidActivityWait = projectType === 'android' ? testRunHandler.androidActivityWait : null;
        const nativeAppMetadata = testRunHandler.nativeAppMetadata;

        if (!nativeAppMetadata) {
            throw new Error(
                'Application not specified. Please provide the required application for execution and try again.',
            );
        }
        try {
            const sessionTags: appiumCapabilitiesBuilder.SessionTags = {
                testName: this.testName,
                executionId: this.executionId,
                executionName: testRunHandler.executionName,
                testResultId: this.testResultId!,
                project: `${this.options.projectData!.name}-${this.options.project}`,
                ...(this.options.customTag && { customTag: this.options.customTag }),
                testId: this.testId!,
                branch: this.branch!,
            };
            const appiumOptions: appiumCapabilitiesBuilder.AppiumOptionsParams = {
                projectData: this.options.projectData,
                projectType,
                nativeAppMetadata,
                appPath,
                androidActivityWait,
                sessionTags,
                gridInfo,
                options: this.options,
                testRunConfig: this.testRunConfig,
                shouldRunInEnhancedMode,
                customCapabilities: testRunHandler.customCapabilities,
            };
            let capabilities: RemoteOptions;

            if (this.options.skipLoadBalancer && this.options.deviceUdid) {
                if (this.options.lockDevice) {
                    await mobileGridService.lockDevice(gridInfo, this.options.deviceUdid);
                }
                capabilities = await appiumCapabilitiesBuilder.buildAppiumDirectConnectionOptions(appiumOptions);
            } else {
                capabilities = await appiumCapabilitiesBuilder.buildAppiumLoadBalancerOptions(appiumOptions);
            }
            logger.info('starting appium session', {
                testResultId: this.testResultId,
                executionId: this.executionId,
                sessionTags,
                nativeAppMetadata,
                capabilities: appiumCapabilitiesBuilder.removeAccessDataFromCapabilities(capabilities),
            });
            capabilities.headers = { 'User-Agent': this.getUserAgentString() };
            const serverAddress = this.getServerAddressFromGrid();
            this.activeSession = await driver.remote(capabilities);
            this.device = await mobileGridService.updateDeviceOnRemoteTestResult(
                testRunHandler,
                this.activeSession,
                serverAddress,
                this.options,
                shouldRunInEnhancedMode,
            );
            const sessionUrl = `${serverAddress}/session/${this.activeSession.sessionId}`;
            this.updateSlotData(sessionUrl);

            logger.info('appium session started successfully', {
                sessionId: this.activeSession.sessionId,
                testResultId: this.testResultId,
                executionId: this.executionId,
                sessionCaps: this.activeSession.capabilities,
            });
        } catch (err) {
            //TODO: catch app status validation
            logger.error('failed to start application', {
                testResultId: this.testResultId,
                executionId: this.executionId,
                err,
            });
            throw err;
        }
    }

    private startKeepAlive(appiumApi: AppiumApi) {
        const { errors } = getSessionPlayer();
        const keepAlive = async () => {
            try {
                await appiumApi.getOrientation();
            } catch (err) {
                logger.error('failed to keep appium session alive', { err });
                if (errors?.AppiumSessionTerminatedError && err instanceof errors.AppiumSessionTerminatedError) {
                    this.stopKeepAlive();
                }
            }
        };
        // VMG default session timeout is 30 seconds
        this.keepAliveIndex = setInterval(keepAlive, 25_000);
    }

    private stopKeepAlive() {
        if (this.keepAliveIndex) {
            clearTimeout(this.keepAliveIndex);
        }
    }

    private updateSlotData(sessionUrl: string) {
        try {
            if (this.options.slotService === 'redis') {
                concurrencyCompanyService.updateSlotDataCache(
                    this.options.company!.companyId,
                    this.options.projectData!.projectId,
                    {
                        testResultId: this.testResultId,
                        executionId: this.executionId,
                        type: 'execution',
                        source: this.options.source,
                        sessionUrl,
                    },
                    this.id,
                );
            }
        } catch (err) {
            logger.error('failed to update slot data', { err });
        }
    }

    private getServerAddressFromGrid() {
        const { host, port, accessToken, type } = this.options.gridData!;
        if (type === gridTypes.TESTIM_TDC) {
            return `https://${host}:${port}/v0/${accessToken}/wd/hub`;
        }

        return `https://${host}:${port}/wd/hub`;
    }

    private getDirectAddressConnection() {
        const { protocol, hostname, port, path } = this.activeSession.options;
        if (protocol && hostname && port && path) {
            return `${protocol}://${hostname}:${port}${path}`;
        }
        return undefined;
    }

    async runTestOnce(testRunHandler: TestRunHandler, appiumTestPlayer: AppiumTestPlayer) {
        const { manifestVersion, AppiumApi, localAssetService } = getSessionPlayer();
        const { sessionPlayer } = appiumTestPlayer;
        const version = manifestVersion || 'runner';
        const testTimeout = this.options.timeout;

        reporter.onWaitToTestComplete(this.id);

        sessionPlayer.playbackManager.executionId = testRunHandler.executionId;
        sessionPlayer.playbackManager.executionName = testRunHandler.executionName;

        let serverAddress;
        if (this.options.skipLoadBalancer) {
            const deviceId = this.options.deviceUdid || (this.testRunConfig as any).mobile?.staticAllocation.deviceUdid;
            const { protocol, hostname, port, path } = await mobileGridService.getTDCDeviceConnection(
                this.options.gridData!,
                deviceId,
            );
            serverAddress = `${protocol}://${hostname}:${port}${path}`;
        } else {
            serverAddress = this.getDirectAddressConnection() || this.getServerAddressFromGrid();
        }
        const capabilities = this.activeSession.capabilities as WebdriverAppiumSessionCapabilities;
        sessionPlayer.playbackManager.appiumSessionData = {
            // @ts-expect-error The type comes from Clickim with the value - but we can't import value from Clickim.
            env: 'remote',
            capabilities,
            sessionId: this.activeSession.sessionId,
            sessionToken: this.options.gridData!.accessToken,
            gridType: this.options.gridData!.type,
            projectType: this.options.projectData!.type,
            gridId: this.options.gridData!.gridId,
            companyId: this.options.company?.companyId,
            device: this.device,
            nativeApp: testRunHandler.nativeApp as unknown as NativeAppData,
        };
        sessionPlayer.playbackManager.appiumApi = new AppiumApi(
            serverAddress,
            this.activeSession.sessionId,
            DOMParser,
            this.device?.domMode === 'enhanced',
        );
        this.startKeepAlive(sessionPlayer.playbackManager.appiumApi);
        localAssetService.initialize({ serverUrl: this.options.localRCASaver });
        const runAppiumTest = async () => {
            try {
                const testResult = await new Promise<TestResult | FailedSessionPlayerTestResult>((resolve, reject) => {
                    sessionPlayer
                        .playByTestId({
                            testId: this.testId!,
                            executionId: this.executionId,
                            resultId: this.testResultId!,
                            baseUrl: this.baseUrl,
                            userData: this.userData!,
                            version,
                            resolve,
                            isLocalRun: false,
                            overrideTestConfigId: this.overrideTestConfigId,
                            branch: this.branch!,
                            remoteRunId: testRunHandler.remoteRunId,
                        })
                        .catch(reject);
                });
                this.stopKeepAlive();
                await localAssetService.drain();
                testResult.stepsResults = null;
                testResult.resultId = this.testResultId!;
                const resultWithStats = { ...testResult, ...testRunHandler.seleniumPerfStats.getStats() };
                return resultWithStats;
            } catch (err) {
                logger.error('error while running appium tests', {
                    testResultId: this.testResultId,
                    executionId: this.executionId,
                    err,
                });
                this.stopKeepAlive();
                throw err;
            }
        };
        try {
            await super.runTestOnce(testRunHandler, appiumTestPlayer);
            return await utils.promiseTimeout(
                runAppiumTest(),
                testTimeout,
                timeoutMessages.TEST_REACHED_CUSTOM_TIMEOUT_MSG,
            );
        } catch (err) {
            logger.error('failed to run test once', { err });
            throw err;
        }
    }

    private getUserAgentString() {
        if (global.meta?.IS_RELEASE) {
            return `Testim/production:${utils.getRunnerVersion()}` as const;
        }
        if (!global.meta?.IS_LOCAL && global.meta?.BRANCH_NAME) {
            return `Testim/CI-Branch:${global.meta.BRANCH_NAME}` as const;
        }
        return 'Testim/local' as const;
    }
}
